import { useQuery } from "@tanstack/react-query";
import { thumbnailDownloadUrl } from "@/shared/lib/firebaseStorage";

/**
 * Resolve stored 250 px thumbnails for a set of image URLs.
 *
 * Why: asset photos synced from the forms pipeline are full-resolution originals
 * (~2.4 MB each, measured in prod), so a 26-photo asset pulls ~62 MB just to
 * paint a row of 64 px squares. Each original has a sibling `_th.jpg` at ~10 KB —
 * the same thing robotic-imaging-web renders its own photo grids from
 * (`PhotoFieldRenderer` uses `photo.thumbnailUrl || photo.url`).
 *
 * Deliberately not the `images-resizeImage` proxy: that puts a Cloud Function in
 * the render path, and it has no `Cache-Control` and re-downloads the full
 * original per call. ENG-1186 rolled the AI pipeline off exactly that pattern
 * after ~90 concurrent requests produced 26 timeouts; a 478-photo property would
 * be worse. The stored thumbnail costs no compute at all.
 *
 * Returns a url→thumbnailUrl map holding only the URLs that have a thumbnail, so
 * callers fall back with `map.get(url) ?? url`.
 */
export function useThumbnailUrls(urls: string[]) {
  // Sorted + deduped so a re-render that reorders the photos doesn't refetch.
  const keys = Array.from(new Set(urls.filter(Boolean))).sort();

  return useQuery<Map<string, string>>({
    queryKey: ["thumbnail-urls", keys],
    enabled: keys.length > 0,
    // Thumbnails are immutable except when we rotate one, and rotation
    // invalidates this key explicitly.
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const resolved = await Promise.all(
        keys.map(async (url) => [url, await thumbnailDownloadUrl(url)] as const),
      );
      const map = new Map<string, string>();
      for (const [url, thumb] of resolved) {
        if (thumb) map.set(url, thumb);
      }
      return map;
    },
  });
}
